import { createContext, type ReactNode, useContext } from "react";

import { useMediaQuery } from "usehooks-ts";

import { BuiFloatingActionBar } from "@btnet/blueprint-components";
import { IconCaretSmallUp, IconX } from "@btnet/blueprint-icons";
import { cn } from "@btnet/utilities-core";

import { BTButton } from "../BTButton/BTButton";
import { BTDropdown } from "../BTDropdown/BTDropdown";
import { BTMenu } from "../BTMenu/BTMenu";
import { BTPopover } from "../BTPopover/BTPopover";
import "./BTFloatingActionBar.less";
import {
  type IBTFloatingActionBarActionProps,
  type IBTFloatingActionBarDividerProps,
  type IBTFloatingActionBarIconActionProps,
  type IBTFloatingActionBarMoreActionsItemProps,
  type IBTFloatingActionBarMoreActionsProps,
  type IBTFloatingActionBarProps,
  type TBTFloatingActionBarClickHandler,
} from "./BTFloatingActionBar.types";

// Lets sub-components read the bar's testid and current collapse state without
// prop-drilling or child cloning. Each sub-component renders itself for the
// active layout — an inline control when expanded, a menu node when collapsed —
// so consumers just compose the tree and the layout resolves through context.
interface IFabContext {
  testid: string;
  isCollapsed: boolean;
  /**
   * The testid prefix for items rendered inside an overflow/combined dropdown
   * menu. Set by {@link OverflowDropdown} to its own testid so a menu item reads
   * as `{menuTestid}-{id}` (e.g. `{root}-more-{id}` in the expanded overflow,
   * `{root}-actions-{id}` in the collapsed combined menu). Undefined for inline
   * actions, which key off the root `testid` instead.
   */
  menuTestid?: string | undefined;
}

const FabContext = createContext<IFabContext>({
  testid: "",
  isCollapsed: false,
});

// The vertical rule between inline groups. mx-2 (8px) on top of the bar's 8px
// gap yields ~16px on each side, matching the legacy BdsFloatingActionBar.
// Shared by the composable `Divider` and the root's built-in leading divider.
const FabDividerRule = ({ className }: { className?: string | undefined }) => (
  <div
    aria-hidden="true"
    className={cn("mx-2 h-8 self-center border-l border-blue-5", className)}
  />
);

// The one menu item shape every action collapses to. `Action`, `IconAction`,
// and `MoreActionsItem` all render this — a text-only item keyed off the action
// `id`, dropping any inline-only affordances (icons, tooltips).
const FabMenuItem = ({
  testid,
  id,
  children,
  onClick,
  icon,
  danger = false,
  disabled = false,
}: {
  testid: string;
  id: string;
  children: ReactNode;
  onClick: TBTFloatingActionBarClickHandler;
  icon?: ReactNode;
  danger?: boolean;
  disabled?: boolean;
}) => (
  <BTMenu.Item
    testid={`${testid}-${id}`}
    icon={icon}
    danger={danger}
    disabled={disabled}
    // BuiMenu hardcodes the item text color (incl. on :hover), swallowing antd's
    // built-in danger red. Re-assert it with a utility: the Tailwind `utilities`
    // layer beats BuiMenu's app-layer rule in every state, so no :hover override
    // or !important is needed. text-red-70 matches the legacy BdsFloatingActionBar.
    className={cn({ "text-red-70": danger })}
    onClick={() => void onClick()}
  >
    {children}
  </BTMenu.Item>
);

/**
 * A text bulk action with an optional leading icon. Inline (at/above `md`) it
 * renders as a text button ("{icon} {label}"); when the bar collapses below `md`
 * it renders itself as a text-only item in the combined "Actions" dropdown (the
 * icon is dropped). Use for higher-priority, always-labeled actions. For
 * icon-only inline actions, see {@link BTFloatingActionBarIconAction}.
 *
 * @example
 * <BTFloatingActionBar.Action id="send" icon={<IconPaperPlaneRight />} onClick={onSend}>
 *   Send
 * </BTFloatingActionBar.Action>
 */
const BTFloatingActionBarAction = ({
  id,
  children,
  onClick,
  icon,
  type = "tertiary",
  danger = false,
  disabled = false,
  ...buttonProps
}: IBTFloatingActionBarActionProps) => {
  const { testid, isCollapsed, menuTestid } = useContext(FabContext);

  // Collapsed: a text-only menu item in the root's combined dropdown, keyed off
  // that dropdown's testid (`{root}-actions-{id}`).
  if (isCollapsed) {
    return (
      <FabMenuItem
        testid={menuTestid ?? testid}
        id={id}
        onClick={onClick}
        danger={danger}
        disabled={disabled}
      >
        {children}
      </FabMenuItem>
    );
  }

  return (
    <BTButton
      {...buttonProps}
      data-testid={`${testid}-${id}`}
      type={type}
      onClick={(event) => void onClick(event)}
      icon={icon}
      disabled={disabled}
      className={cn({ "BTFloatingActionBar-danger": danger })}
    >
      {children}
    </BTButton>
  );
};

/**
 * An icon-only bulk action. Inline (at/above `md`) it renders icon-only, with
 * the label (`children`) shown as a hover/focus tooltip (via `BTPopover`) and
 * used as the accessible name; when the bar collapses below `md` it renders
 * itself as a text-only item in the combined "Actions" dropdown. Use for
 * compact, icon-only actions. For always-labeled actions, see
 * {@link BTFloatingActionBarAction}.
 *
 * @example
 * <BTFloatingActionBar.IconAction id="delete" icon={<IconTrash />} onClick={onDelete}>
 *   Delete
 * </BTFloatingActionBar.IconAction>
 */
const BTFloatingActionBarIconAction = ({
  id,
  children,
  icon,
  onClick,
  type = "tertiary",
  danger = false,
  disabled = false,
  ...buttonProps
}: IBTFloatingActionBarIconActionProps) => {
  const { testid, isCollapsed, menuTestid } = useContext(FabContext);

  // Collapsed: a text-only menu item (the icon is dropped), keyed off the
  // combined dropdown's testid (`{root}-actions-{id}`).
  if (isCollapsed) {
    return (
      <FabMenuItem
        testid={menuTestid ?? testid}
        id={id}
        onClick={onClick}
        danger={danger}
        disabled={disabled}
      >
        {children}
      </FabMenuItem>
    );
  }

  return (
    <BTPopover content={children} placement="top">
      <BTButton
        {...buttonProps}
        data-testid={`${testid}-${id}`}
        type={type}
        onClick={(event) => void onClick(event)}
        icon={icon}
        disabled={disabled}
        aria-label={typeof children === "string" ? children : undefined}
        className={cn({ "BTFloatingActionBar-danger": danger })}
      />
    </BTPopover>
  );
};

/**
 * A separator between groups of actions. Inline (at/above `md`) it renders a
 * vertical rule between inline groups; when the bar collapses it renders a
 * horizontal `BTMenu.Divider` at the same position in the combined dropdown.
 */
const BTFloatingActionBarDivider = ({
  className,
}: IBTFloatingActionBarDividerProps) => {
  const { isCollapsed } = useContext(FabContext);

  if (isCollapsed) {
    // bg-gray-20 is the border-default color; the Tailwind `utilities` layer
    // beats BuiMenu's app-layer default fill, so no .less override is needed.
    return <BTMenu.Divider className="bg-gray-20" />;
  }

  return <FabDividerRule className={className} />;
};

/**
 * A single item inside a {@link BTFloatingActionBar.MoreActions} group. Renders
 * a `BTMenu.Item` — inside its group's own dropdown while the bar is expanded,
 * or inside the root's combined "Actions" dropdown once it collapses.
 */
const BTFloatingActionBarMoreActionsItem = ({
  id,
  children,
  onClick,
  icon,
  danger = false,
  disabled = false,
}: IBTFloatingActionBarMoreActionsItemProps) => {
  const { testid, menuTestid } = useContext(FabContext);

  return (
    <FabMenuItem
      // Menu items key off the containing dropdown's testid (`{root}-more` when
      // expanded, `{root}-actions` when collapsed) so they read as
      // `{root}-more-{id}` / `{root}-actions-{id}`, falling back to the root.
      testid={menuTestid ?? testid}
      id={id}
      onClick={onClick}
      icon={icon}
      danger={danger}
      disabled={disabled}
    >
      {children}
    </FabMenuItem>
  );
};

/**
 * The dropdown trigger + menu used both for the expanded "More actions" group
 * and the root's collapsed combined "Actions" menu. Its children self-render as
 * `BTMenu.Item`s / `BTMenu.SubMenu`s. The menu opens upward, so the trigger
 * caret points up.
 *
 * The trigger renders an optional leading `icon` (through the button's `icon`
 * prop, matching the inline icon actions), the `triggerLabel` content, and, by
 * default, a trailing upward caret. Set `caret={false}` to drop the caret — e.g.
 * for an icon-only trigger that should sit flush with the surrounding icon
 * actions. Pass `ariaLabel` to name the trigger when it has no text label.
 */
const OverflowDropdown = ({
  testid,
  triggerLabel,
  icon,
  ariaLabel,
  caret = true,
  children,
}: {
  testid: string;
  triggerLabel?: ReactNode;
  icon?: ReactNode;
  ariaLabel?: string | undefined;
  caret?: boolean | undefined;
  children: ReactNode;
}) => {
  const parent = useContext(FabContext);

  return (
    <BTDropdown
      testid={`${testid}-dropdown`}
      placement="topCenter"
      overlay={
        <BTMenu
          testid={`${testid}-menu`}
          aria-label="More options"
          className="BTFloatingActionBar-menu"
        >
          {/* Items inside this menu self-render off `menuTestid`, so they read as
              `{testid}-{id}` (e.g. `{root}-more-{id}` / `{root}-actions-{id}`). */}
          <FabContext.Provider value={{ ...parent, menuTestid: testid }}>
            {children}
          </FabContext.Provider>
        </BTMenu>
      }
      trigger={["click"]}
    >
      <BTButton
        data-testid={`${testid}-trigger`}
        type="tertiary"
        icon={icon}
        aria-label={ariaLabel}
      >
        {(triggerLabel || caret) && (
          <span className="flex items-center gap-1 whitespace-nowrap">
            {triggerLabel}
            {caret && <IconCaretSmallUp />}
          </span>
        )}
      </BTButton>
    </BTDropdown>
  );
};

/**
 * A dropdown menu group in the bar. While the bar is expanded it renders its
 * own trigger + menu ("More actions ▴" by default, or a custom `label` like
 * "New ▴"). Pass an `icon` (rendered through the button's `icon` prop, like the
 * inline icon actions) for an icon-only trigger — with `ariaLabel` for the
 * accessible name and `caret={false}` so it sits flush with the icon actions.
 *
 * Once the bar collapses below `md`, it folds into the root's combined "Actions"
 * dropdown: a default group (no `label`) flattens its items in place, while a
 * custom-labeled group (e.g. "Attach to") is preserved as a labeled submenu.
 *
 * A bar may contain multiple groups — e.g. a "New" dropdown next to the "More
 * actions" overflow. Give each a unique `id` so their testids don't collide.
 *
 * @example
 * <BTFloatingActionBar.MoreActions id="new" label="New">
 *   <BTFloatingActionBar.MoreActionsItem id="bill" onClick={onNewBill}>
 *     Bill
 *   </BTFloatingActionBar.MoreActionsItem>
 * </BTFloatingActionBar.MoreActions>
 * <BTFloatingActionBar.MoreActions
 *   id="export"
 *   icon={<IconDownloadSimple />}
 *   ariaLabel="Export"
 *   caret={false}
 * >
 *   <BTFloatingActionBar.MoreActionsItem id="csv" onClick={onExportCsv}>
 *     CSV
 *   </BTFloatingActionBar.MoreActionsItem>
 * </BTFloatingActionBar.MoreActions>
 * <BTFloatingActionBar.MoreActions>
 *   <BTFloatingActionBar.MoreActionsItem id="void" danger onClick={onVoid}>
 *     Void
 *   </BTFloatingActionBar.MoreActionsItem>
 *   <BTFloatingActionBar.MoreActionsItem id="attach" onClick={onAttach}>
 *     Attach files
 *   </BTFloatingActionBar.MoreActionsItem>
 * </BTFloatingActionBar.MoreActions>
 */
const BTFloatingActionBarMoreActions = ({
  children,
  icon,
  label,
  ariaLabel,
  caret,
  id = "more",
}: IBTFloatingActionBarMoreActionsProps) => {
  const { testid, isCollapsed } = useContext(FabContext);

  if (isCollapsed) {
    // A custom label keeps the group as a labeled submenu in the combined menu;
    // the default (no label) flattens the items in place. The icon is dropped —
    // the collapsed menu is text-only, like every other action.
    if (label === undefined) {
      return <>{children}</>;
    }
    return (
      <BTMenu.SubMenu testid={`${testid}-${id}`} title={label}>
        {children}
      </BTMenu.SubMenu>
    );
  }

  // Expanded: this group's items under its own trigger. `id` keeps the testids
  // distinct when a bar has more than one MoreActions group. Fall back to the
  // "More actions" text only when there's no icon to stand in for it.
  return (
    <OverflowDropdown
      testid={`${testid}-${id}`}
      icon={icon}
      triggerLabel={label ?? (icon ? undefined : "More actions")}
      ariaLabel={ariaLabel}
      caret={caret}
    >
      {children}
    </OverflowDropdown>
  );
};

/**
 * BTFloatingActionBar - the app-facing, list-page floating action bar.
 *
 * A composable compound component mirroring the legacy `BdsFloatingActionBar`:
 * the root owns open/visibility and the responsive collapse, while its content
 * is assembled from the sub-components — `Action`, `IconAction`, `Divider`, and
 * `MoreActions` / `MoreActionsItem`. Each sub-component renders itself for the
 * active layout: at/above the `md` breakpoint the inline `Action`s show on the
 * left and the optional `MoreActions` dropdown on the right; below `md` every
 * action renders as an item inside a single combined "Actions" dropdown, with
 * no loss of any action.
 *
 * @param props - {@link IBTFloatingActionBarProps} controlling selection, actions, and styling.
 * @returns The rendered floating action bar.
 * @example
 * <BTFloatingActionBar
 *   testid="bill-action-bar"
 *   selectedCount={selected.length}
 *   onDeselect={clearSelection}
 * >
 *   <BTFloatingActionBar.IconAction id="print" icon={<IconPrinter />} onClick={onPrint}>
 *     Print
 *   </BTFloatingActionBar.IconAction>
 *   <BTFloatingActionBar.Divider />
 *   <BTFloatingActionBar.Action id="mark-ready" onClick={onMarkReady}>
 *     Mark ready for payment
 *   </BTFloatingActionBar.Action>
 *   <BTFloatingActionBar.MoreActions>
 *     <BTFloatingActionBar.MoreActionsItem id="void" danger onClick={onVoid}>
 *       Void
 *     </BTFloatingActionBar.MoreActionsItem>
 *   </BTFloatingActionBar.MoreActions>
 * </BTFloatingActionBar>
 */

const BTFloatingActionBarInternal = ({
  selectedCount,
  onDeselect,
  children,
  ariaLabel = "Grid actions",
  className,
  testid,
}: IBTFloatingActionBarProps) => {
  const isCollapsed = !useMediaQuery("(min-width: 768px)");

  return (
    <BuiFloatingActionBar
      isOpen={selectedCount > 0}
      ariaLabel={ariaLabel}
      testid={testid}
      className={cn("BTFloatingActionBar gap-2", className)}
    >
      <FabContext.Provider value={{ testid, isCollapsed }}>
        <span className="whitespace-nowrap">{selectedCount} selected</span>
        <BTButton
          data-testid={`${testid}-deselect`}
          type="tertiary"
          aria-label="Uncheck all items"
          onClick={onDeselect}
          icon={<IconX />}
        />
        <FabDividerRule />

        {/* Expanded: children self-render inline. Collapsed: they self-render as
            menu nodes inside a single combined "Actions" dropdown. */}
        {isCollapsed ? (
          <OverflowDropdown testid={`${testid}-actions`} triggerLabel="Actions">
            {children}
          </OverflowDropdown>
        ) : (
          children
        )}
      </FabContext.Provider>
    </BuiFloatingActionBar>
  );
};

/**
 * BTFloatingActionBar with its composable sub-components attached. See
 * {@link BTFloatingActionBarInternal} for usage.
 */
export const BTFloatingActionBar = Object.assign(BTFloatingActionBarInternal, {
  /** A separator between action groups (vertical inline, horizontal in the menu). See {@link BTFloatingActionBarDivider}. */
  Divider: BTFloatingActionBarDivider,
  /** A text action with an optional leading icon. See {@link BTFloatingActionBarAction}. */
  Action: BTFloatingActionBarAction,
  /** An icon-only action (tooltip label). See {@link BTFloatingActionBarIconAction}. */
  IconAction: BTFloatingActionBarIconAction,
  /** The explicit overflow dropdown. See {@link BTFloatingActionBarMoreActions}. */
  MoreActions: BTFloatingActionBarMoreActions,
  /** An item inside the MoreActions dropdown. See {@link BTFloatingActionBarMoreActionsItem}. */
  MoreActionsItem: BTFloatingActionBarMoreActionsItem,
});
